import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getStudentLedger } from '../../api/payments';
import { PaymentType } from '../../types/payment';

const TYPE_COLOR: Record<PaymentType, 'primary' | 'info' | 'success' | 'warning'> = {
  PAYMENT: 'primary',
  DISCOUNT: 'info',
  SCHOLARSHIP: 'success',
  REFUND: 'warning',
};

interface StudentLedgerDialogProps {
  studentId: number | null;
  onClose: () => void;
}

export const StudentLedgerDialog = ({ studentId, onClose }: StudentLedgerDialogProps) => {
  const ledgerQuery = useQuery({
    queryKey: ['student-ledger', studentId],
    queryFn: () => getStudentLedger(studentId!),
    enabled: studentId !== null,
  });

  const ledger = ledgerQuery.data;

  return (
    <Dialog open={studentId !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Student Ledger</DialogTitle>
      <DialogContent>
        {ledgerQuery.isLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
        {ledger && (
          <Stack spacing={3} sx={{ pb: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Outstanding balance
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {ledger.summary.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No charges configured (course has no tuition fee set).
                  </Typography>
                )}
                {ledger.summary.map((s) => (
                  <Paper key={s.currency.id} variant="outlined" sx={{ p: 1.5, minWidth: 160 }}>
                    <Typography variant="h6">
                      {s.currency.symbol ?? s.currency.code} {s.outstandingBalance.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Charges {s.totalCharges.toFixed(2)} — Collected {s.totalCollected.toFixed(2)} — Discounts{' '}
                      {s.totalDiscounts.toFixed(2)} — Refunded {s.totalRefunded.toFixed(2)}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Charges by application
              </Typography>
              {ledger.charges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No applications with a tuition fee configured.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Application</TableCell>
                      <TableCell>University / Course</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.charges.map((c) => (
                      <TableRow key={c.applicationId}>
                        <TableCell>{c.applicationNumber}</TableCell>
                        <TableCell>
                          {c.university} / {c.course}
                        </TableCell>
                        <TableCell align="right">{c.tuitionFee.toFixed(2)}</TableCell>
                        <TableCell align="right">{c.paid.toFixed(2)}</TableCell>
                        <TableCell align="right">{c.outstanding.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Transaction history
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Payment #</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Running balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledger.entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{new Date(e.paymentDate).toLocaleDateString()}</TableCell>
                      <TableCell>{e.paymentNumber}</TableCell>
                      <TableCell>
                        <Chip size="small" label={e.type} color={TYPE_COLOR[e.type]} />
                      </TableCell>
                      <TableCell>{e.category.name}</TableCell>
                      <TableCell align="right">
                        {e.currency.symbol ?? e.currency.code} {e.amount.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{e.runningBalance.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {ledger.entries.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No transactions yet.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
